interface HRSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  userName: string;
  onLogout: () => void;
}

const navItems = [
  { id: 'manager', icon: 'ri-user-heart-line', label: 'Мой менеджер' },
  { id: 'uploads', icon: 'ri-upload-cloud-2-line', label: 'Загрузка откликов' },
  { id: 'payments', icon: 'ri-wallet-3-line', label: 'Мои выплаты' },
  { id: 'requisites', icon: 'ri-file-list-3-line', label: 'Мои реквизиты', locked: true },
  { id: 'calculator', icon: 'ri-calculator-line', label: 'Калькулятор ЗП' },
];

export default function HRSidebar({ activeSection, onSectionChange, userName, onLogout }: HRSidebarProps) {
  const initials = userName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('');

  return (
    <aside className="w-72 flex-shrink-0 bg-[#000000] border-r border-[#18181c] flex flex-col min-h-screen">
      {/* Brand */}
      <div className="px-6 py-5 border-b border-[#18181c] flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-primary-500/10 border border-primary-500/20">
          <i className="ri-shield-user-line text-primary-300 text-lg" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] text-foreground-500 tracking-widest uppercase">Корпоративный</span>
          <span className="text-sm font-semibold text-white">Портал сотрудников</span>
        </div>
      </div>

      {/* Profile block */}
      <div className="px-6 py-6 border-b border-[#18181c]">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-300 to-accent-400 flex items-center justify-center text-white font-bold text-lg select-none">
              {initials || <i className="ri-user-star-line text-xl" />}
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-accent-200 rounded-full border-2 border-[#000000]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{userName}</p>
            <p className="text-xs text-accent-300/80 mt-0.5">HR — менеджер</p>
          </div>
        </div>

        {/* Status badge */}
        <div className="mt-4 flex items-center gap-2 bg-accent-300/10 border border-accent-300/20 rounded-xl px-3 py-2.5">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-accent-200 text-sm" />
          </span>
          <span className="text-xs text-accent-200 leading-tight">
            Процесс трудоустройства ещё не завершен, часть функционала недоступна
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-accent-300/15 text-accent-200 border border-accent-300/20'
                  : 'text-foreground-600 hover:text-foreground-800 hover:bg-[#0e0e11]'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`} />
              </span>
              {item.label}
              {item.locked && (
                <span className="ml-auto w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-xs text-foreground-400" />
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-4 py-4 border-t border-[#18181c]">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-500 hover:text-foreground-700 hover:bg-[#0e0e11] transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-r-line" />
          </span>
          Выйти из аккаунта
        </button>
      </div>

      {/* Bottom branding */}
      <div className="px-6 py-4 border-t border-[#18181c]">
        <div className="relative">
          <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />
        </div>
        <p className="text-[10px] text-foreground-400 tracking-widest uppercase">© 2026 · Корпоративный портал</p>
      </div>
    </aside>
  );
}
